import React, { useState, useEffect } from 'react';
import { Container, Card, Spinner, Alert, Button, Form, InputGroup, Modal, Dropdown } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp, FaSort, FaSortUp, FaSortDown, FaSearch, FaSave, FaFileUpload, FaColumns, FaDownload, FaSync } from 'react-icons/fa';
import { Table, Collapse } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { 
  getUploadedEmployeesWithAmounts,
  getSnapshotEmployeeData,
  getStagingEmployees,
  getImportedEmployees,
  getUserRoles,
  updateEmployeeAmounts,
  bulkUpdateEmployeeAmounts,
  triggerDataSync,
  getCurrentOrgId, 
  ORG_ID_CHANGE_EVENT 
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const REMCHANNEL_COLORS = {
  steelBlue: '#003366',
  green: '#00A651',
  black: '#000000',
  paper: '#FFFFFF',
  orange: '#FF6B00',
  rust: '#C35214',
  blue: '#1B3664',
  grey: '#666666',
  // Tints/Shades
  steelBlue80: '#33587A',
  steelBlue60: '#668BAF',
  steelBlue40: '#99BFD4',
  steelBlue20: '#CCDFF0',
  green80: '#33B86B',
  green60: '#66CB8C',
  green40: '#99DEAD',
  green20: '#CCF1CE',
  orange80: '#FF8A33',
  orange60: '#FFA866',
  orange40: '#FFC699',
  orange20: '#FFE4CC',
  rust80: '#D16B33',
  rust60: '#E18566',
  rust40: '#F1A099',
  rust20: '#F9CABB',
  blue80: '#495A7A',
  blue60: '#7A8BAF',
  blue40: '#AABFD4',
  blue20: '#D5DFF0',
  grey80: '#888888',
  grey60: '#AAAAAA',
  grey40: '#CCCCCC',
  grey20: '#EEEEEE',
};

const THEME = {
  // Primary greens
  primary: '#00A651', // Main green
  primaryDark: '#008c44', // Darker green for hover states
  primaryDarker: '#006633', // Even darker green for active states
  primaryLight: '#e8f5e9', // Light green for backgrounds
  primaryLighter: '#f1f8e9', // Even lighter green for subtle backgrounds
  primaryBorder: '#4caf50', // Green for borders
  
  // Grey tones
  grey900: '#212529', // Darkest grey for primary text
  grey800: '#343a40', // Dark grey for secondary text
  grey700: '#495057', // Grey for headings
  grey600: '#6c757d', // Grey for secondary text
  grey500: '#adb5bd', // Grey for borders
  grey400: '#ced4da', // Light grey for disabled states
  grey300: '#dee2e6', // Lighter grey for borders
  grey200: '#e9ecef', // Very light grey for backgrounds
  grey100: '#f8f9fa', // Lightest grey for backgrounds
  
  // Status colors
  success: '#00A651', // Success green
  warning: '#ffc107', // Warning yellow
  danger: '#dc3545', // Danger red
  info: '#17a2b8', // Info blue
  
  // Base colors
  background: '#ffffff',
  backgroundAlt: '#f8f9fa',
  border: '#dee2e6',
  shadow: 'rgba(0, 0, 0, 0.1)'
};

// Reusable DataTable Component
const DataTable = ({ 
  title, 
  data, 
  columns, 
  allColumns = null, // All columns including hidden ones for search
  defaultOpen = true,
  searchable = true,
  pageSize = 10,
  collapsible = false
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);

  // Use allColumns for search if provided, otherwise use visible columns
  const searchColumns = allColumns || columns;

  // Filter data based on search term - search across ALL columns (including hidden ones)
  const filteredData = React.useMemo(() => {
    if (!searchTerm) return data;
    const searchLower = searchTerm.toLowerCase();
    return data.filter(row => 
      searchColumns.some(col => {
        const value = col.accessor ? col.accessor(row) : row[col.key];
        // Handle different data types
        if (value === null || value === undefined) return false;
        // Convert to string and search
        const valueStr = typeof value === 'object' ? JSON.stringify(value) : String(value);
        return valueStr.toLowerCase().includes(searchLower);
      })
    );
  }, [data, searchTerm, searchColumns]);

  // Sort data
  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = columns.find(c => c.key === sortConfig.key)?.accessor 
        ? columns.find(c => c.key === sortConfig.key).accessor(a)
        : a[sortConfig.key];
      const bValue = columns.find(c => c.key === sortConfig.key)?.accessor
        ? columns.find(c => c.key === sortConfig.key).accessor(b)
        : b[sortConfig.key];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      const aStr = aValue.toString().toLowerCase();
      const bStr = bValue.toString().toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig, columns]);

  // Paginate data
  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ms-1" style={{ opacity: 0.3 }} />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ms-1" /> 
      : <FaSortDown className="ms-1" />;
  };

  const tableContent = (
    <Card.Body>
      {searchable && (
        <InputGroup className="mb-3">
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder={allColumns ? "Search across all columns..." : "Search..."}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </InputGroup>
      )}
      
      <div className="table-responsive">
        <Table hover striped bordered>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    cursor: col.sortable !== false ? 'pointer' : 'default',
                    userSelect: 'none',
                    fontSize: '12px'
                  }}
                  onClick={() => col.sortable !== false && handleSort(col.key)}
                >
                  {col.header}
                  {col.sortable !== false && getSortIcon(col.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row, index) => {
                // Check if record is only in SnapshotEmployeeData (not in UploadedEmployees)
                const isSnapshotOnly = !row.UploadedEmployeeID && row.SnapshotEmployeeDataID;
                return (
                  <tr 
                    key={row.UploadedEmployeeID || row.id || index}
                    style={{
                      backgroundColor: isSnapshotOnly ? 'rgba(13, 202, 240, 0.1)' : undefined
                    }}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        style={{
                          fontSize: '12px'
                        }}
                      >
                        {col.render 
                          ? col.render(row[col.key], row)
                          : (col.accessor ? col.accessor(row) : row[col.key] ?? '-')
                        }
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center" style={{ fontSize: '12px' }}>
                  {searchTerm ? 'No results found' : 'No data available'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} entries
          </div>
          <div>
            <Button
              variant="outline-success"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
              className="me-2"
            >
              Previous
            </Button>
            <span className="mx-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline-success"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </Card.Body>
  );

  if (!collapsible) {
    return (
      <Card className="mb-4">
        {tableContent}
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <Card.Header 
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(!open)}
      >
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{title}</h5>
          <span>
            {open ? <FaChevronUp /> : <FaChevronDown />}
          </span>
        </div>
      </Card.Header>
      <Collapse in={open}>
        <div>
          {tableContent}
        </div>
      </Collapse>
    </Card>
  );
};

const DataAugmentation = ({ orgId: propOrgId }) => {
  const { isAuthenticated } = useAuth();
  const [uploadedEmployees, setUploadedEmployees] = useState([]);
  const [employeeAmounts, setEmployeeAmounts] = useState({}); // Map of uploadedEmployeeId -> { ltiAmount, ontargetLtiAmount }
  const [editingAmounts, setEditingAmounts] = useState({}); // Map of uploadedEmployeeId -> { ltiAmount, ontargetLtiAmount }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [progress, setProgress] = useState(null); // Progress tracking for bulk updates
  const [orgId, setOrgId] = useState(propOrgId || getCurrentOrgId());
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [bulkUpdateData, setBulkUpdateData] = useState(''); // CSV-like data for bulk update
  const fileInputRef = React.useRef(null);
  const [hasAllowedRole, setHasAllowedRole] = useState(false);
  const isEmbedded = window.self !== window.top;
  
  // Get default table from environment variable, fallback to 'uploaded'
  const getDefaultTable = () => {
    const envDefault = process.env.REACT_APP_DEFAULT_EMPLOYEE_TABLE || 
                       window.ENV?.REACT_APP_DEFAULT_EMPLOYEE_TABLE || 
                       'uploaded';
    return ['uploaded', 'staging', 'snapshot', 'imported'].includes(envDefault) ? envDefault : 'uploaded';
  };
  
  const [selectedEmployeeTable, setSelectedEmployeeTable] = useState(getDefaultTable());
  const [tableSwitchNotification, setTableSwitchNotification] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    UploadedEmployeeID: true,
    EmployeeNumber: true,
    FirstName: true,
    LastName: true,
    OrganizationJobGrade: true,
    GradeName: true,
    JobName: true,
    TGP: true, // Show TGP column by default
    STI: true,
    OnTargetSTI: true,
    LTIAmount: true,
    STIAmount: true,
    // All others default to false
    OrganizationID: false,
    CostCentre: false,
    DateOfBirth: false,
    OrganizationJobCode: false,
    OrganizationJobTitle: false,
    JobID: false,
    SnapshotEmployeeDataID: false
  });

  // Check user roles for KAM access
  useEffect(() => {
    const checkUserRoles = async () => {
      if (orgId) {
        try {
          const roles = await getUserRoles();
          // Only apply role restrictions in embedded mode
          if (isEmbedded) {
            const allowedRoles = [1, 2, 3, 4, 5]; // KAM roles
            const hasRole = roles.some((role) =>
              allowedRoles.includes(role.RoleID)
            );
            setHasAllowedRole(hasRole);
          } else {
            // In non-embedded mode, always allow access
            setHasAllowedRole(true);
          }
        } catch (error) {
          console.error("Error checking user roles:", error);
          // Default to false on error in embedded mode
          setHasAllowedRole(!isEmbedded);
        }
      }
    };

    checkUserRoles();
  }, [orgId, isEmbedded]);

  useEffect(() => {
    const handleOrgIdChange = (event) => {
      const detail = event.detail;
      const newOrgId = typeof detail === 'object' ? (detail.orgId || getCurrentOrgId()) : detail || getCurrentOrgId();
      setOrgId(newOrgId);
      if (isAuthenticated) {
        setLoading(true);
      }
    };

    window.addEventListener(ORG_ID_CHANGE_EVENT, handleOrgIdChange);
    return () => {
      window.removeEventListener(ORG_ID_CHANGE_EVENT, handleOrgIdChange);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated || !orgId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        let mergedData = [];
        
        // Fetch data based on selected table
        switch (selectedEmployeeTable) {
          case 'uploaded':
            mergedData = await getUploadedEmployeesWithAmounts(orgId);
            break;
          case 'staging':
            mergedData = await getStagingEmployees(orgId);
            // Staging employees don't have LTI amounts, so we'll set empty
            break;
          case 'snapshot':
            mergedData = await getSnapshotEmployeeData(orgId);
            // Snapshot employees don't have LTI amounts, so we'll set empty
            break;
          case 'imported':
            mergedData = await getImportedEmployees(orgId);
            // Imported employees don't have LTI amounts, so we'll set empty
            // Map Firstname/Lastname to FirstName/LastName for consistency
            mergedData = mergedData.map(emp => ({
              ...emp,
              FirstName: emp.Firstname || emp.FirstName,
              LastName: emp.Lastname || emp.LastName
            }));
            break;
          default:
            mergedData = await getUploadedEmployeesWithAmounts(orgId);
        }

        // Create a map of amounts by ID for editing (only for uploaded table)
        const amountsMap = {};
        if (selectedEmployeeTable === 'uploaded') {
          mergedData.forEach(emp => {
            const id = emp.UploadedEmployeeID || emp.SnapshotEmployeeDataID;
            if (id) {
              amountsMap[id] = {
                ltiAmount: emp.ltiAmount ?? null,
                ontargetLtiAmount: emp.ontargetLtiAmount ?? null
              };
            }
          });
        }

        setUploadedEmployees(mergedData);
        setEmployeeAmounts(amountsMap);
        setEditingAmounts({});
      } catch (err) {
        console.error(`Error fetching ${selectedEmployeeTable} employees:`, err);
        setError(err.response?.data?.message || err.message || `Failed to fetch ${selectedEmployeeTable} employees`);
        setUploadedEmployees([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orgId, isAuthenticated, selectedEmployeeTable]);

  const handleAmountChange = (uploadedEmployeeId, field, value) => {
    // Store the raw value as string for now, we'll parse it on save
    // This ensures we don't lose the value if it's 0 or empty
    const newValue = value === '' || value === null || value === undefined ? null : value;
    setEditingAmounts(prev => ({
      ...prev,
      [uploadedEmployeeId]: {
        ...prev[uploadedEmployeeId],
        [field]: newValue
      }
    }));
  };

  const handleSaveAmounts = async (uploadedEmployeeId) => {
    // Only allow saving for uploaded employees
    if (selectedEmployeeTable !== 'uploaded') {
      setError('Editing is only available for Uploaded Employees table');
      return;
    }
    
    // Find employee by UploadedEmployeeID or SnapshotEmployeeDataID
    const employee = uploadedEmployees.find(emp => 
      emp.UploadedEmployeeID === uploadedEmployeeId || 
      emp.SnapshotEmployeeDataID === uploadedEmployeeId
    );
    if (!employee) return;

    const edited = editingAmounts[uploadedEmployeeId];
    if (!edited) {
      return;
    }

    let ltiAmount = null; // Start with null, only set if user has edited
    // Check if edited value exists (including 0, which is falsy but valid)
    if (edited.ltiAmount !== undefined) {
      // User has edited - use their value (including 0)
      if (edited.ltiAmount === null || edited.ltiAmount === '') {
        ltiAmount = 0; // User cleared it, save as 0
      } else {
        const parsed = typeof edited.ltiAmount === 'string' ? parseFloat(edited.ltiAmount) : Number(edited.ltiAmount);
        ltiAmount = isNaN(parsed) ? 0 : parsed;
      }
    } else if (employee.ltiAmount !== undefined && employee.ltiAmount !== null) {
      // No edit, use existing value from database
      const parsed = typeof employee.ltiAmount === 'string' ? parseFloat(employee.ltiAmount) : Number(employee.ltiAmount);
      ltiAmount = isNaN(parsed) ? 0 : parsed;
    } else {
      // No value in database, default to 0
      ltiAmount = 0;
    }

    let ontargetLtiAmount = null; // Start with null, only set if user has edited
    // Check if edited value exists (including 0, which is falsy but valid)
    if (edited.ontargetLtiAmount !== undefined) {
      // User has edited - use their value (including 0)
      if (edited.ontargetLtiAmount === null || edited.ontargetLtiAmount === '') {
        ontargetLtiAmount = 0; // User cleared it, save as 0
      } else {
        const parsed = typeof edited.ontargetLtiAmount === 'string' ? parseFloat(edited.ontargetLtiAmount) : Number(edited.ontargetLtiAmount);
        ontargetLtiAmount = isNaN(parsed) ? 0 : parsed;
      }
    } else if (employee.ontargetLtiAmount !== undefined && employee.ontargetLtiAmount !== null) {
      // No edit, use existing value from database
      const parsed = typeof employee.ontargetLtiAmount === 'string' ? parseFloat(employee.ontargetLtiAmount) : Number(employee.ontargetLtiAmount);
      ontargetLtiAmount = isNaN(parsed) ? 0 : parsed;
    } else {
      // No value in database, default to 0
      ontargetLtiAmount = 0;
    }

    const employeeNumber = employee.EmployeeNumber || '';
    // Use UploadedEmployeeID if available, otherwise use SnapshotEmployeeDataID
    const idToUse = employee.UploadedEmployeeID || employee.SnapshotEmployeeDataID || uploadedEmployeeId;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const result = await updateEmployeeAmounts(orgId, idToUse, ltiAmount, ontargetLtiAmount, employeeNumber);

      // Refresh data from database to ensure we have the latest values
      try {
        const amountsData = await getUploadedEmployeesWithAmounts(orgId);
        const amountsMap = {};
        amountsData.forEach(emp => {
          const id = emp.UploadedEmployeeID || emp.SnapshotEmployeeDataID;
          if (id) {
            amountsMap[id] = {
              ltiAmount: emp.ltiAmount ?? null,
              ontargetLtiAmount: emp.ontargetLtiAmount ?? null
            };
          }
        });

        // Update local state with fresh data from database
        setUploadedEmployees(prev => prev.map(emp => {
          const id = emp.UploadedEmployeeID || emp.SnapshotEmployeeDataID;
          return {
            ...emp,
            ltiAmount: amountsMap[id]?.ltiAmount ?? null,
            ontargetLtiAmount: amountsMap[id]?.ontargetLtiAmount ?? null
          };
        }));

        setEmployeeAmounts(amountsMap);
      } catch (refreshErr) {
        console.warn('Error refreshing data after save:', refreshErr);
        // Fallback to local state update if refresh fails
        setUploadedEmployees(prev => prev.map(emp => {
          const empId = emp.UploadedEmployeeID || emp.SnapshotEmployeeDataID;
          return empId === uploadedEmployeeId
            ? { ...emp, ltiAmount, ontargetLtiAmount }
            : emp;
        }));

        setEmployeeAmounts(prev => ({
          ...prev,
          [uploadedEmployeeId]: { ltiAmount, ontargetLtiAmount }
        }));
      }

      // Clear editing state for this employee
      setEditingAmounts(prev => {
        const next = { ...prev };
        delete next[uploadedEmployeeId];
        return next;
      });

      setSuccess(`Amounts updated for employee ${employeeNumber || idToUse}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating amounts:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to update amounts';
      setError(`Error: ${errorMessage}. Please check the browser console and backend logs for details.`);
      console.error('Full error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        employeeId: idToUse,
        employeeNumber: employeeNumber,
        ltiAmount: ltiAmount,
        ontargetLtiAmount: ontargetLtiAmount
      });
      setTimeout(() => setError(null), 10000);
    } finally {
      setSaving(false);
    }
  };

  const handleSyncData = async () => {
    if (!orgId) {
      setError('Organization ID is required');
      return;
    }

    try {
      setSyncing(true);
      setError(null);
      setSuccess(null);

      const result = await triggerDataSync(orgId);
      setSuccess(`Sync job queued successfully. Job ID: ${result.jobId}. Refreshing data...`);
      
      // Poll for data refresh - try a few times with increasing delays
      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 3000; // 3 seconds
      
      const pollForData = async () => {
        attempts++;
        try {
          const mergedData = await getUploadedEmployeesWithAmounts(orgId);
          const amountsMap = {};
          mergedData.forEach(emp => {
            const id = emp.UploadedEmployeeID || emp.SnapshotEmployeeDataID;
            if (id) {
              amountsMap[id] = {
                ltiAmount: emp.ltiAmount ?? null,
                ontargetLtiAmount: emp.ontargetLtiAmount ?? null
              };
            }
          });
          setUploadedEmployees(mergedData);
          setEmployeeAmounts(amountsMap);
          setEditingAmounts({});
          setSuccess('Data synced and refreshed successfully');
          setSyncing(false);
        } catch (refreshError) {
          console.error(`Error refreshing data (attempt ${attempts}):`, refreshError);
          if (attempts < maxAttempts) {
            // Try again after delay
            setTimeout(pollForData, pollInterval);
          } else {
            setSuccess('Sync job queued. Data may take a moment to update. Please refresh the page if needed.');
            setSyncing(false);
          }
        }
      };
      
      // Start polling after initial delay
      setTimeout(pollForData, pollInterval);
    } catch (err) {
      console.error('Error triggering sync:', err);
      setError(err.response?.data?.message || err.message || 'Failed to trigger data sync');
      setSyncing(false);
    }
  };

  const handleDownloadCSV = () => {
    try {
      // Get visible columns
      const visibleCols = allColumns.filter(col => visibleColumns[col.key] !== false);
      
      // Create CSV header - include ID, Employee Number, Company Grade, and LTI/On Target LTI amounts
      const headers = ['UploadedEmployeeID', 'EmployeeNumber', 'CompanyGrade', 'LTIAmount', 'OnTargetLTIAmount'];
      const csvRows = [headers.join(',')];

      // Add data rows
      uploadedEmployees.forEach(emp => {
        const row = [
          emp.UploadedEmployeeID || '',
          emp.EmployeeNumber || '',
          emp.OrganizationJobGrade || '',
          emp.ltiAmount !== null && emp.ltiAmount !== undefined ? emp.ltiAmount : '',
          emp.ontargetLtiAmount !== null && emp.ontargetLtiAmount !== undefined ? emp.ontargetLtiAmount : ''
        ];
        csvRows.push(row.join(','));
      });

      // Create CSV content
      const csvContent = csvRows.join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `employee-amounts-${orgId}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccess('CSV file downloaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error downloading CSV:', err);
      setError('Failed to download CSV file');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isCSV = fileName.endsWith('.csv') || file.type.includes('csv') || file.type.includes('text');
    const isXLSX = fileName.endsWith('.xlsx') || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const isXLS = fileName.endsWith('.xls') || file.type === 'application/vnd.ms-excel';

    // Check if file is supported format
    if (!isCSV && !isXLSX && !isXLS) {
      setError('Please upload a CSV, XLS, or XLSX file');
      setTimeout(() => setError(null), 5000);
      return;
    }

    if (isCSV) {
      // Handle CSV files with comma or colon delimiters
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          
          // Try to detect delimiter by checking first few lines
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length === 0) {
            setError('CSV file appears to be empty');
            setTimeout(() => setError(null), 5000);
            return;
          }
          
          // Check first non-empty line for delimiter
          const firstLine = lines[0];
          const commaCount = (firstLine.match(/,/g) || []).length;
          const colonCount = (firstLine.match(/:/g) || []).length;
          
          // Use papaparse to parse CSV with detected delimiter
          // Default to comma if counts are equal or both are zero
          const delimiter = colonCount > commaCount ? ':' : ',';
          
          Papa.parse(text, {
            delimiter: delimiter,
            header: false,
            skipEmptyLines: true,
            transform: (value) => {
              // Trim whitespace from each cell
              return typeof value === 'string' ? value.trim() : value;
            },
            complete: (results) => {
              try {
                if (!results.data || results.data.length === 0) {
                  setError('No data found in CSV file');
                  setTimeout(() => setError(null), 5000);
                  return;
                }
                
                // Convert parsed data back to CSV-like format for the textarea
                // Expected format: uploadedEmployeeId,employeeNumber,ltiAmount,ontargetLtiAmount
                const csvLines = results.data
                  .map(row => {
                    // Filter out null/undefined/empty cells but keep the row structure
                    const filteredRow = row
                      .map(cell => {
                        const cellValue = cell === null || cell === undefined ? '' : String(cell);
                        return cellValue.trim();
                      })
                      .filter((cell, index, arr) => {
                        // Keep cell if it's not empty, or if there are non-empty cells after it
                        return cell !== '' || arr.slice(index + 1).some(c => c !== '');
                      });
                    return filteredRow.join(',');
                  })
                  .filter(line => line.trim() !== '');
                
                if (csvLines.length === 0) {
                  setError('No valid data rows found in CSV file');
                  setTimeout(() => setError(null), 5000);
                  return;
                }
                
                const csvText = csvLines.join('\n');
                setBulkUpdateData(csvText);
                setSuccess(`CSV file loaded successfully (${delimiter === ',' ? 'comma' : 'colon'} delimiter detected, ${csvLines.length} rows)`);
                setTimeout(() => setSuccess(null), 3000);
              } catch (err) {
                console.error('Error processing CSV data:', err);
                setError('Failed to process CSV file');
                setTimeout(() => setError(null), 5000);
              }
            },
            error: (error) => {
              console.error('Error parsing CSV:', error);
              setError(`Failed to parse CSV file: ${error.message || 'Unknown error'}`);
              setTimeout(() => setError(null), 5000);
            }
          });
        } catch (err) {
          console.error('Error reading CSV file:', err);
          setError('Failed to read CSV file');
          setTimeout(() => setError(null), 5000);
        }
      };

      reader.onerror = () => {
        setError('Error reading file');
        setTimeout(() => setError(null), 5000);
      };

      reader.readAsText(file);
    } else if (isXLSX || isXLS) {
      // Handle Excel files (.xls and .xlsx)
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
            setError('Excel file appears to have no sheets');
            setTimeout(() => setError(null), 5000);
            return;
          }
          
          // Get the first sheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          if (!worksheet) {
            setError('Failed to read worksheet from Excel file');
            setTimeout(() => setError(null), 5000);
            return;
          }
          
          // Convert to JSON array (header: 1 means first row is data, not headers)
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            header: 1, 
            defval: '',
            raw: false // Convert dates and numbers to strings
          });
          
          if (!jsonData || jsonData.length === 0) {
            setError('Excel file appears to be empty');
            setTimeout(() => setError(null), 5000);
            return;
          }
          
          // Convert to CSV-like format for the textarea
          // Expected format: uploadedEmployeeId,employeeNumber,ltiAmount,ontargetLtiAmount
          const csvLines = jsonData
            .filter(row => {
              // Filter out completely empty rows
              if (!row || row.length === 0) return false;
              // Check if row has at least one non-empty cell
              return Array.isArray(row) && row.some(cell => {
                const cellValue = cell === null || cell === undefined ? '' : String(cell);
                return cellValue.trim() !== '';
              });
            })
            .map(row => {
              // Convert row to array and process cells
              const rowArray = Array.isArray(row) ? row : [row];
              const processedRow = rowArray
                .map(cell => {
                  // Convert cell to string and trim
                  const cellValue = cell === null || cell === undefined ? '' : String(cell);
                  return cellValue.trim();
                })
                .filter((cell, index, arr) => {
                  // Keep cell if it's not empty, or if there are non-empty cells after it
                  return cell !== '' || arr.slice(index + 1).some(c => c !== '');
                });
              return processedRow.join(',');
            })
            .filter(line => line.trim() !== '');
          
          if (csvLines.length === 0) {
            setError('No valid data rows found in Excel file');
            setTimeout(() => setError(null), 5000);
            return;
          }
          
          const csvText = csvLines.join('\n');
          setBulkUpdateData(csvText);
          setSuccess(`${isXLSX ? 'XLSX' : 'XLS'} file loaded successfully (${csvLines.length} rows)`);
          setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
          console.error('Error reading Excel file:', err);
          setError(`Failed to read Excel file: ${err.message || 'Unknown error'}`);
          setTimeout(() => setError(null), 5000);
        }
      };

      reader.onerror = () => {
        setError('Error reading file');
        setTimeout(() => setError(null), 5000);
      };

      reader.readAsArrayBuffer(file);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBulkUpdate = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      setProgress(null);

      // Parse bulk update data (format: uploadedEmployeeId,employeeNumber,companyGrade,ltiAmount,ontargetLtiAmount)
      const lines = bulkUpdateData.trim().split('\n').filter(line => line.trim());
      const updates = [];
      const ignoredRows = [];
      const invalidRows = [];

      // Create a map of existing employees for quick lookup
      const employeeMap = new Map();
      uploadedEmployees.forEach(emp => {
        // Map by uploadedEmployeeId
        employeeMap.set(emp.UploadedEmployeeID, emp);
        // Also map by employeeNumber for validation
        if (emp.EmployeeNumber) {
          employeeMap.set(`empNum:${emp.EmployeeNumber}`, emp);
        }
      });

      // Skip header row if present
      const dataLines = lines.filter((line, index) => {
        // Skip first line if it looks like a header
        if (index === 0 && line.toLowerCase().includes('uploadedemployeeid')) {
          return false;
        }
        return true;
      });

      for (let i = 0; i < dataLines.length; i++) {
        const line = dataLines[i];
        const parts = line.split(',').map(p => p.trim());
        
        if (parts.length < 2) {
          invalidRows.push({ line: i + 1, reason: 'Insufficient columns' });
          continue;
        }

        const uploadedEmployeeIdStr = parts[0];
        const employeeNumber = parts[1];
        const companyGrade = parts[2] || ''; // Company Grade (column C) - read but not used for validation
        const ltiAmountStr = parts[3] || '';
        const ontargetLtiAmountStr = parts[4] || '';

        // Validate uploadedEmployeeId
        const uploadedEmployeeId = parseInt(uploadedEmployeeIdStr, 10);
        if (isNaN(uploadedEmployeeId) || uploadedEmployeeId <= 0) {
          invalidRows.push({ 
            line: i + 1, 
            reason: `Invalid UploadedEmployeeID: ${uploadedEmployeeIdStr}` 
          });
          continue;
        }

        // Validate employeeNumber
        if (!employeeNumber || employeeNumber === '') {
          invalidRows.push({ 
            line: i + 1, 
            reason: 'Missing EmployeeNumber' 
          });
          continue;
        }

        // Check if employee exists
        const existingEmployee = employeeMap.get(uploadedEmployeeId);
        if (!existingEmployee) {
          ignoredRows.push({ 
            uploadedEmployeeId, 
            employeeNumber, 
            reason: 'Employee not found' 
          });
          continue;
        }

        // Validate employeeNumber matches
        if (existingEmployee.EmployeeNumber !== employeeNumber) {
          ignoredRows.push({ 
            uploadedEmployeeId, 
            employeeNumber, 
            reason: `EmployeeNumber mismatch. Expected: ${existingEmployee.EmployeeNumber}, Got: ${employeeNumber}` 
          });
          continue;
        }

        // Parse amounts (allow empty/null values, but no negative values)
        let ltiAmount = null;
        if (ltiAmountStr && ltiAmountStr !== '') {
          const parsedLti = parseFloat(ltiAmountStr);
          if (!isNaN(parsedLti)) {
            if (parsedLti < 0) {
              invalidRows.push({ 
                line: i + 1, 
                reason: `LTIAmount cannot be negative: ${ltiAmountStr}` 
              });
              continue;
            }
            ltiAmount = parsedLti;
          } else {
            invalidRows.push({ 
              line: i + 1, 
              reason: `Invalid LTIAmount: ${ltiAmountStr}` 
            });
            continue;
          }
        }

        let ontargetLtiAmount = null;
        if (ontargetLtiAmountStr && ontargetLtiAmountStr !== '') {
          const parsedOntargetLti = parseFloat(ontargetLtiAmountStr);
          if (!isNaN(parsedOntargetLti)) {
            if (parsedOntargetLti < 0) {
              invalidRows.push({ 
                line: i + 1, 
                reason: `OnTargetLTIAmount cannot be negative: ${ontargetLtiAmountStr}` 
              });
              continue;
            }
            ontargetLtiAmount = parsedOntargetLti;
          } else {
            invalidRows.push({ 
              line: i + 1, 
              reason: `Invalid OnTargetLTIAmount: ${ontargetLtiAmountStr}` 
            });
            continue;
          }
        }

        // All validations passed, add to updates
        updates.push({
          uploadedEmployeeId,
          employeeNumber,
          ltiAmount,
          ontargetLtiAmount
        });
      }

      if (updates.length === 0) {
        let errorMsg = 'No valid updates found.';
        if (ignoredRows.length > 0) {
          errorMsg += ` ${ignoredRows.length} employee(s) not found and were ignored.`;
        }
        if (invalidRows.length > 0) {
          errorMsg += ` ${invalidRows.length} row(s) had validation errors.`;
        }
        setError(errorMsg);
        setSaving(false);
        return;
      }

      // Show progress for large updates
      const showProgress = updates.length > 500;
      
      const result = await bulkUpdateEmployeeAmounts(orgId, updates, {
        chunkSize: 500, // Process 500 at a time
        maxRetries: 3,
        retryDelay: 2000,
        onProgress: showProgress ? (progress) => {
          setProgress({
            message: `Processing chunk ${progress.chunk}/${progress.totalChunks}... (${progress.processed}/${progress.total} records)`,
            percentage: Math.round((progress.processed / progress.total) * 100),
            updated: progress.updated,
            created: progress.created,
            errors: progress.errors
          });
        } : undefined
      });

      // Refresh data
      const amountsData = await getUploadedEmployeesWithAmounts(orgId);
      const amountsMap = {};
      amountsData.forEach(emp => {
        const id = emp.UploadedEmployeeID || emp.SnapshotEmployeeDataID;
        if (id) {
          amountsMap[id] = {
            ltiAmount: emp.ltiAmount,
            ontargetLtiAmount: emp.ontargetLtiAmount
          };
        }
      });

      setUploadedEmployees(prev => prev.map(emp => {
        const id = emp.UploadedEmployeeID || emp.SnapshotEmployeeDataID;
        return {
          ...emp,
          ltiAmount: amountsMap[id]?.ltiAmount ?? null,
          ontargetLtiAmount: amountsMap[id]?.ontargetLtiAmount ?? null
        };
      }));

      setEmployeeAmounts(amountsMap);
      setEditingAmounts({});
      setShowBulkUpdateModal(false);
      setBulkUpdateData('');

      // Build success message with validation details
      let successMsg = `Bulk update completed: ${result.updated} updated, ${result.created} created`;
      
      if (ignoredRows.length > 0) {
        successMsg += `. ${ignoredRows.length} employee(s) not found and were ignored`;
      }
      
      if (invalidRows.length > 0) {
        successMsg += `. ${invalidRows.length} row(s) had validation errors`;
      }
      
      if (result.errors && result.errors.length > 0) {
        successMsg += `. ${result.errors.length} error(s) during update`;
      }

      setSuccess(successMsg);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error bulk updating:', err);
      
      // Provide more specific error messages for connection issues
      let errorMessage = err.response?.data?.message || err.message || 'Failed to bulk update';
      
      if (err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK') {
        errorMessage = 'Connection error. Please check your network connection and try again.';
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. The file may be too large. Try splitting it into smaller batches or check your connection.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again in a few moments.';
      }
      
      setError(errorMessage);
      const errorDisplayTime = (err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') ? 10000 : 5000;
      setTimeout(() => setError(null), errorDisplayTime);
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">
          Please log in to view this data.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading uploaded employees...</p>
      </Container>
    );
  }

  if (error && !uploadedEmployees.length) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  // Define all available columns
  const allColumns = [
    {
      key: 'UploadedEmployeeID',
      header: 'ID',
      accessor: (row) => {
        // Handle different ID fields based on table source
        if (selectedEmployeeTable === 'uploaded') {
          return row.UploadedEmployeeID ?? row.SnapshotEmployeeDataID ?? '-';
        } else if (selectedEmployeeTable === 'staging') {
          return row.OrganizationID && row.EmployeeNumber 
            ? `${row.OrganizationID}-${row.EmployeeNumber}` 
            : row.EmployeeNumber ?? '-';
        } else if (selectedEmployeeTable === 'snapshot') {
          return row.SnapshotEmployeeDataID ?? row.EmployeeNumber ?? '-';
        } else if (selectedEmployeeTable === 'imported') {
          return row.ImportedEmployeeID ?? row.EmployeeNumber ?? '-';
        }
        return row.UploadedEmployeeID ?? row.SnapshotEmployeeDataID ?? '-';
      }
    },
    {
      key: 'SnapshotEmployeeDataID',
      header: 'Snapshot ID',
      accessor: (row) => row.SnapshotEmployeeDataID ?? '-'
    },
    {
      key: 'OrganizationID',
      header: 'Org ID',
      accessor: (row) => row.OrganizationID ?? '-'
    },
    {
      key: 'EmployeeNumber',
      header: 'Employee Number',
      accessor: (row) => row.EmployeeNumber ?? '-',
      render: (value, row) => {
        // Check if record is only in SnapshotEmployeeData (not in UploadedEmployees)
        const isSnapshotOnly = !row.UploadedEmployeeID && row.SnapshotEmployeeDataID;
        // Check if record has snapshot_employee_data_id (published) - show in uploaded table
        const isPublished = row.SnapshotEmployeeDataID != null && selectedEmployeeTable === 'uploaded';
        
        return (
          <span>
            {value !== '-' ? value : '-'}
            {isSnapshotOnly && (
              <span 
                className="badge bg-info ms-2" 
                style={{ fontSize: '10px' }}
                title="This employee record exists only in SnapshotEmployeeData (not in UploadedEmployees). STI values come from snapshot data."
              >
                Snapshot Only
              </span>
            )}
            {hasAllowedRole && isPublished && (
              <span 
                className="badge bg-success ms-2" 
                style={{ fontSize: '10px' }}
                title="This employee record has been published (snapshot_employee_data_id is available in uploaded_employees table)."
              >
                Published
              </span>
            )}
          </span>
        );
      }
    },
    {
      key: 'CostCentre',
      header: 'Cost Center',
      accessor: (row) => row.CostCentre ?? '-'
    },
    {
      key: 'FirstName',
      header: 'First Name',
      accessor: (row) => row.FirstName ?? '-',
      render: (value, row) => (
        <span style={{ 
          filter: 'blur(4px)',
          userSelect: 'none',
          pointerEvents: 'none'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'LastName',
      header: 'Last Name',
      accessor: (row) => row.LastName ?? '-',
      render: (value, row) => (
        <span style={{ 
          filter: 'blur(4px)',
          userSelect: 'none',
          pointerEvents: 'none'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'DateOfBirth',
      header: 'DOB',
      accessor: (row) => {
        const value = row.DateOfBirth;
        if (value === null || value === undefined) return '-';
        if (value instanceof Date) {
          try {
            return value.toLocaleDateString();
          } catch {
            return String(value);
          }
        }
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              return date.toLocaleDateString();
            }
          } catch {
            return value;
          }
        }
        return String(value);
      }
    },
    {
      key: 'OrganizationJobGrade',
      header: 'Company Grade',
      accessor: (row) => row.OrganizationJobGrade ?? '-'
    },
    {
      key: 'GradeName',
      header: 'Survey Grade',
      accessor: (row) => row.GradeName ?? '-'
    },
    {
      key: 'OrganizationJobCode',
      header: 'Job Code',
      accessor: (row) => row.OrganizationJobCode ?? '-'
    },
    {
      key: 'OrganizationJobTitle',
      header: 'Company Job Title',
      accessor: (row) => row.OrganizationJobTitle ?? '-'
    },
    {
      key: 'STI',
      header: 'STI Amount',
      accessor: (row) => {
        const value = row.STI;
        if (value === null || value === undefined) return '-';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (!isNaN(numValue) && typeof numValue === 'number') {
          return new Intl.NumberFormat('en-ZA', {
            style: 'currency',
            currency: 'ZAR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(numValue);
        }
        return typeof value === 'number' ? new Intl.NumberFormat('en-ZA', {
          style: 'currency',
          currency: 'ZAR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value) : String(value);
      },
      render: (value, row) => {
        const stiValue = row.STI;
        const isSnapshotOnly = !row.UploadedEmployeeID && row.SnapshotEmployeeDataID;
        const hasMissingSTI = isSnapshotOnly && (stiValue === null || stiValue === undefined || stiValue === 0);
        
        let formattedValue = '-';
        if (stiValue !== null && stiValue !== undefined) {
          const numValue = typeof stiValue === 'string' ? parseFloat(stiValue) : stiValue;
          if (!isNaN(numValue) && typeof numValue === 'number') {
            formattedValue = new Intl.NumberFormat('en-ZA', {
              style: 'currency',
              currency: 'ZAR',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(numValue);
          } else if (typeof stiValue === 'number') {
            formattedValue = new Intl.NumberFormat('en-ZA', {
              style: 'currency',
              currency: 'ZAR',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(stiValue);
          } else {
            formattedValue = String(stiValue);
          }
        }
        return (
          <span style={{ fontSize: '12px' }}>
            {formattedValue}
            {hasMissingSTI && (
              <span 
                className="badge bg-warning ms-1" 
                style={{ fontSize: '9px' }}
                title="Snapshot-only record with missing STI value. Expected from SnapshotEmployeeData but not found."
              >
                No STI
              </span>
            )}
          </span>
        );
      }
    },
    {
      key: 'OnTargetSTI',
      header: 'On Target STI Amount',
      accessor: (row) => {
        const value = row.OnTargetSTI;
        if (value === null || value === undefined) return '-';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (!isNaN(numValue) && typeof numValue === 'number') {
          return new Intl.NumberFormat('en-ZA', {
            style: 'currency',
            currency: 'ZAR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(numValue);
        }
        return typeof value === 'number' ? new Intl.NumberFormat('en-ZA', {
          style: 'currency',
          currency: 'ZAR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value) : String(value);
      },
      render: (value, row) => {
        const onTargetStiValue = row.OnTargetSTI;
        const isSnapshotOnly = !row.UploadedEmployeeID && row.SnapshotEmployeeDataID;
        const hasMissingOnTargetSTI = isSnapshotOnly && (onTargetStiValue === null || onTargetStiValue === undefined || onTargetStiValue === 0);
        
        let formattedValue = '-';
        if (onTargetStiValue !== null && onTargetStiValue !== undefined) {
          const numValue = typeof onTargetStiValue === 'string' ? parseFloat(onTargetStiValue) : onTargetStiValue;
          if (!isNaN(numValue) && typeof numValue === 'number') {
            formattedValue = new Intl.NumberFormat('en-ZA', {
              style: 'currency',
              currency: 'ZAR',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(numValue);
          } else if (typeof onTargetStiValue === 'number') {
            formattedValue = new Intl.NumberFormat('en-ZA', {
              style: 'currency',
              currency: 'ZAR',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(onTargetStiValue);
          } else {
            formattedValue = String(onTargetStiValue);
          }
        }
        return (
          <span style={{ fontSize: '12px' }}>
            {formattedValue}
            {hasMissingOnTargetSTI && (
              <span 
                className="badge bg-warning ms-1" 
                style={{ fontSize: '9px' }}
                title="Snapshot-only record with missing On Target STI value. Expected from SnapshotEmployeeData but not found."
              >
                No On Target STI
              </span>
            )}
          </span>
        );
      }
    },
    {
      key: 'LTIAmount',
      header: 'LTI Amount',
      render: (value, row) => {
        // Only allow editing for uploaded employees
        if (selectedEmployeeTable !== 'uploaded') {
          return <span style={{ fontSize: '12px', color: REMCHANNEL_COLORS.grey }}>— (Read-only)</span>;
        }
        
        const uploadedEmployeeId = row.UploadedEmployeeID || row.SnapshotEmployeeDataID;
        const edited = editingAmounts[uploadedEmployeeId];
        // Default to 0 if null/undefined, otherwise use edited value or row value
        const currentValue = edited?.ltiAmount !== undefined 
          ? (edited.ltiAmount === null ? 0 : edited.ltiAmount)
          : (row.ltiAmount === null || row.ltiAmount === undefined ? 0 : row.ltiAmount);
        const hasChanges = edited && (edited.ltiAmount !== row.ltiAmount || edited.ontargetLtiAmount !== row.ontargetLtiAmount);

        return (
          <div className="d-flex align-items-center gap-2">
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              size="sm"
              style={{ 
                width: '120px',
                fontWeight: '500',
                textAlign: 'right',
                fontSize: '12px',
                paddingRight: '8px'
              }}
              value={currentValue === null || currentValue === undefined || currentValue === '' ? 0 : currentValue}
              onChange={(e) => {
                const value = e.target.value;
                // Prevent negative values
                if (value === '' || value === null || value === undefined) {
                  handleAmountChange(uploadedEmployeeId, 'ltiAmount', '');
                } else {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue) && numValue >= 0) {
                    handleAmountChange(uploadedEmployeeId, 'ltiAmount', value);
                  }
                }
              }}
              placeholder="0.00"
            />
            {hasChanges && (
              <Button
                variant="success"
                size="sm"
                onClick={() => handleSaveAmounts(uploadedEmployeeId)}
                disabled={saving}
                title="Save changes"
                style={{
                  backgroundColor: THEME.success,
                  borderColor: THEME.success
                }}
              >
                <FaSave />
              </Button>
            )}
          </div>
        );
      }
    },
    {
      key: 'STIAmount',
      header: 'On Target LTI Amount',
      render: (value, row) => {
        // Only allow editing for uploaded employees
        if (selectedEmployeeTable !== 'uploaded') {
          return <span style={{ fontSize: '12px', color: REMCHANNEL_COLORS.grey }}>— (Read-only)</span>;
        }
        
        const uploadedEmployeeId = row.UploadedEmployeeID || row.SnapshotEmployeeDataID;
        const edited = editingAmounts[uploadedEmployeeId];
        // Default to 0 if null/undefined, otherwise use edited value or row value
        const currentValue = edited?.ontargetLtiAmount !== undefined 
          ? (edited.ontargetLtiAmount === null ? 0 : edited.ontargetLtiAmount)
          : (row.ontargetLtiAmount === null || row.ontargetLtiAmount === undefined ? 0 : row.ontargetLtiAmount);
        const hasChanges = edited && (edited.ltiAmount !== row.ltiAmount || edited.ontargetLtiAmount !== row.ontargetLtiAmount);

        return (
          <div className="d-flex align-items-center gap-2">
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              size="sm"
              style={{ 
                width: '120px',
                fontWeight: '500',
                textAlign: 'right',
                fontSize: '12px',
                paddingRight: '8px'
              }}
              value={currentValue === null || currentValue === undefined || currentValue === '' ? 0 : currentValue}
              onChange={(e) => {
                const value = e.target.value;
                // Prevent negative values
                if (value === '' || value === null || value === undefined) {
                  handleAmountChange(uploadedEmployeeId, 'ontargetLtiAmount', '');
                } else {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue) && numValue >= 0) {
                    handleAmountChange(uploadedEmployeeId, 'ontargetLtiAmount', value);
                  }
                }
              }}
              placeholder="0.00"
            />
            {hasChanges && (
              <Button
                variant="success"
                size="sm"
                onClick={() => handleSaveAmounts(uploadedEmployeeId)}
                disabled={saving}
                title="Save changes"
                style={{
                  backgroundColor: THEME.success,
                  borderColor: THEME.success
                }}
              >
                <FaSave />
              </Button>
            )}
          </div>
        );
      }
    },
    {
      key: 'TGP',
      header: 'TGP',
      accessor: (row) => {
        // Use TGP from snapshot (prefer snapshot, fallback to uploaded)
        const value = row.TGP;
        if (value === null || value === undefined) return '-';
        if (typeof value === 'number') {
          return new Intl.NumberFormat('en-ZA', {
            style: 'currency',
            currency: 'ZAR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(value);
        }
        // Handle string values from database
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (!isNaN(numValue) && typeof numValue === 'number') {
          return new Intl.NumberFormat('en-ZA', {
            style: 'currency',
            currency: 'ZAR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(numValue);
        }
        return String(value);
      },
      render: (value, row) => {
        // Use TGP from snapshot (prefer snapshot, fallback to uploaded)
        const tgpValue = row.TGP;
        let formattedValue = '-';
        if (tgpValue !== null && tgpValue !== undefined) {
          const numValue = typeof tgpValue === 'string' ? parseFloat(tgpValue) : tgpValue;
          if (!isNaN(numValue) && typeof numValue === 'number') {
            formattedValue = new Intl.NumberFormat('en-ZA', {
              style: 'currency',
              currency: 'ZAR',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(numValue);
          } else if (typeof tgpValue === 'number') {
            formattedValue = new Intl.NumberFormat('en-ZA', {
              style: 'currency',
              currency: 'ZAR',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(tgpValue);
          } else {
            formattedValue = String(tgpValue);
          }
        }
        return (
          <span style={{ fontWeight: '500', color: REMCHANNEL_COLORS.steelBlue, fontSize: '12px' }}>
            {formattedValue}
          </span>
        );
      }
    }
  ];

  // Filter columns based on visibility
  const columns = allColumns.filter(col => visibleColumns[col.key] !== false);

  const handleColumnVisibilityChange = (columnKey, isVisible) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: isVisible
    }));
  };

  const handleTableChange = (newTable) => {
    if (newTable !== selectedEmployeeTable) {
      setSelectedEmployeeTable(newTable);
      setEditingAmounts({}); // Clear any unsaved edits when switching
    }
  };

  return (
    <Container>
      {/* Visual notification for table switch */}
      {tableSwitchNotification && (
        <Alert 
          variant="info" 
          dismissible 
          onClose={() => setTableSwitchNotification(null)}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            minWidth: '300px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}
        >
          <strong>{tableSwitchNotification}</strong>
        </Alert>
      )}
      
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h2 className="mb-0">LTI Data Collection</h2>
        <div className="d-flex gap-2 flex-wrap">
          {hasAllowedRole && (
            <Dropdown>
              <Dropdown.Toggle 
                variant="outline-success"
                size="sm"
                style={{
                  borderColor: THEME.success,
                  color: THEME.success
                }}
              >
                <i className="bi bi-table me-1"></i>
                Data Source
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item 
                  active={selectedEmployeeTable === 'uploaded'}
                  onClick={() => handleTableChange('uploaded')}
                >
                  <i className="bi bi-check-circle me-2" style={{ 
                    visibility: selectedEmployeeTable === 'uploaded' ? 'visible' : 'hidden' 
                  }}></i>
                  Uploaded Employees
                </Dropdown.Item>
                <Dropdown.Item 
                  active={selectedEmployeeTable === 'staging'}
                  onClick={() => handleTableChange('staging')}
                >
                  <i className="bi bi-check-circle me-2" style={{ 
                    visibility: selectedEmployeeTable === 'staging' ? 'visible' : 'hidden' 
                  }}></i>
                  Staging Employees
                </Dropdown.Item>
                <Dropdown.Item 
                  active={selectedEmployeeTable === 'snapshot'}
                  onClick={() => handleTableChange('snapshot')}
                >
                  <i className="bi bi-check-circle me-2" style={{ 
                    visibility: selectedEmployeeTable === 'snapshot' ? 'visible' : 'hidden' 
                  }}></i>
                  Snapshot Employee Data
                </Dropdown.Item>
                <Dropdown.Item 
                  active={selectedEmployeeTable === 'imported'}
                  onClick={() => handleTableChange('imported')}
                >
                  <i className="bi bi-check-circle me-2" style={{ 
                    visibility: selectedEmployeeTable === 'imported' ? 'visible' : 'hidden' 
                  }}></i>
                  Imported Employees
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
          {/* Columns dropdown button - commented out per user request */}
          {/* <Dropdown>
            <Dropdown.Toggle 
              variant="outline-success" 
              id="column-toggle"
              style={{
                borderColor: THEME.success,
                color: THEME.success
              }}
            >
              <FaColumns className="me-1" />
              Columns
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ maxHeight: '400px', overflowY: 'auto', padding: '10px' }}>
              <div style={{ padding: '5px' }}>
                <strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>Show/Hide Columns</strong>
                {allColumns.map(col => (
                  <Form.Check
                    key={col.key}
                    type="checkbox"
                    id={`col-${col.key}`}
                    label={col.header}
                    checked={visibleColumns[col.key] !== false}
                    onChange={(e) => handleColumnVisibilityChange(col.key, e.target.checked)}
                    style={{ fontSize: '12px', marginBottom: '5px' }}
                  />
                ))}
              </div>
            </Dropdown.Menu>
          </Dropdown> */}
          <Button 
            variant="success" 
            onClick={handleSyncData}
            disabled={syncing || saving}
            style={{
              backgroundColor: syncing ? THEME.grey300 : THEME.success,
              borderColor: syncing ? THEME.grey300 : THEME.success,
              color: '#fff'
            }}
          >
            {syncing ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Syncing...
              </>
            ) : (
              <>
                <FaSync className="me-1" />
                Pull Latest Data
              </>
            )}
          </Button>
          <Button 
            variant="success" 
            onClick={() => setShowBulkUpdateModal(true)}
            disabled={saving || syncing || selectedEmployeeTable !== 'uploaded'}
            title={selectedEmployeeTable !== 'uploaded' ? 'Bulk update is only available for Uploaded Employees' : 'Bulk update LTI amounts'}
            style={{
              backgroundColor: THEME.success,
              borderColor: THEME.success,
              opacity: selectedEmployeeTable !== 'uploaded' ? 0.5 : 1
            }}
          >
            <FaFileUpload className="me-1" />
            Bulk Update
          </Button>
          <Button 
            variant="outline-success" 
            onClick={() => window.location.reload()}
            disabled={syncing}
            style={{
              borderColor: THEME.success,
              color: THEME.success
            }}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {selectedEmployeeTable !== 'uploaded' && (
        <Alert variant="info" className="mb-3">
          <strong>Read-only mode:</strong> You are viewing {
            selectedEmployeeTable === 'staging' ? 'Staging Employees' : 
            selectedEmployeeTable === 'snapshot' ? 'Snapshot Employee Data' :
            'Imported Employees'
          }. 
          Editing LTI amounts is only available for Uploaded Employees. Switch to "Uploaded Employees" to edit data.
        </Alert>
      )}

      {uploadedEmployees.length > 0 ? (
        <>
          <Alert variant="info" className="mb-3">
            <strong>{uploadedEmployees.length}</strong> uploaded employee{uploadedEmployees.length !== 1 ? 's' : ''} found.
            <br />
            <small>Edit LTI and On Target LTI amounts directly in the table. Click the save button (✓) to save changes for each row.</small>
          </Alert>
          <DataTable
            title="Uploaded Employees"
            data={uploadedEmployees}
            columns={columns}
            allColumns={allColumns}
            collapsible={false}
            searchable={true}
            pageSize={20}
          />
        </>
      ) : (
        <Alert variant="info">
          No uploaded employees found for this organization.
        </Alert>
      )}

      {/* Bulk Update Modal */}
      <Modal 
        show={showBulkUpdateModal} 
        onHide={() => setShowBulkUpdateModal(false)} 
        size="xl"
        style={{ maxWidth: '90vw' }}
        contentClassName="modal-xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Bulk Update LTI/On Target LTI Amounts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <Alert variant="info" className="mb-0 flex-grow-1 me-3">
              <strong>Format:</strong> One line per employee. Each line should contain:
              <br />
              <code>uploadedEmployeeId,employeeNumber,ltiAmount,onTargetLTIAmount</code>
              <br />
              <br />
              <strong>Example:</strong>
              <br />
              <code>
                12345,EMP001,10000.50,5000.25<br />
                12346,EMP002,12000.00,6000.00<br />
                12347,EMP003,,3000.00
              </code>
              <br />
              <br />
              Leave amounts empty or omit them to set to null.
            </Alert>
            <div className="d-flex flex-column gap-2">
              <Button
                variant="outline-primary"
                onClick={handleDownloadCSV}
                style={{
                  borderColor: THEME.primary,
                  color: THEME.primary,
                  whiteSpace: 'nowrap',
                  height: 'fit-content'
                }}
              >
                <FaDownload className="me-1" />
                Download CSV
              </Button>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  id="csv-upload-input"
                />
                <Button
                  variant="outline-primary"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    borderColor: THEME.primary,
                    color: THEME.primary,
                    whiteSpace: 'nowrap',
                    width: '100%'
                  }}
                >
                  <FaFileUpload className="me-1" />
                  Upload File (CSV/XLS/XLSX)
                </Button>
              </div>
            </div>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Bulk Update Data (CSV format)</Form.Label>
            <Form.Control
              as="textarea"
              rows={10}
              value={bulkUpdateData}
              onChange={(e) => setBulkUpdateData(e.target.value)}
              placeholder="uploadedEmployeeId,employeeNumber,ltiAmount,onTargetLTIAmount&#10;12345,EMP001,10000.50,5000.25&#10;12346,EMP002,12000.00,6000.00"
            />
          </Form.Group>
          {progress && (
            <Alert variant="info" className="mb-3">
              <div className="d-flex align-items-center mb-2">
                <Spinner animation="border" size="sm" className="me-2" />
                <strong>{progress.message}</strong>
              </div>
              <div className="progress mb-2" style={{ height: '20px' }}>
                <div 
                  className="progress-bar progress-bar-striped progress-bar-animated" 
                  role="progressbar" 
                  style={{ width: `${progress.percentage}%` }}
                  aria-valuenow={progress.percentage} 
                  aria-valuemin="0" 
                  aria-valuemax="100"
                >
                  {progress.percentage}%
                </div>
              </div>
              {progress.updated > 0 || progress.created > 0 || progress.errors > 0 ? (
                <div className="small">
                  Updated: {progress.updated} | Created: {progress.created} | Errors: {progress.errors}
                </div>
              ) : null}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowBulkUpdateModal(false)}
            style={{
              backgroundColor: THEME.grey200,
              borderColor: THEME.grey300,
              color: THEME.grey800
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleBulkUpdate}
            disabled={saving || !bulkUpdateData.trim()}
            style={{
              backgroundColor: THEME.primary,
              borderColor: THEME.primary
            }}
          >
            {saving ? 'Updating...' : 'Update'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DataAugmentation;
